import {
	Button,
	EmptyState,
	OverflowText,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { CreditsStat } from "~/common/billing/topup";
import { api, type LedgerEntry, type Org } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";

/**
 * A stored key's sentence, or the key itself when this panel has none.
 *
 * The reasons and kinds the server writes are known, small sets, but the
 * columns are open strings, and one this panel has no sentence for is shown as
 * it was stored rather than as a bare missing-string key.
 */
function stored(t: (key: string) => string, table: string, raw: string): string {
	const label = t(`${table}.${raw}`);
	return label === `${table}.${raw}` ? raw : label;
}

/**
 * What the line was for, as specifically as the row can say it.
 *
 * "Models run for you" is not an answer to "on what", so where the reference
 * led somewhere that knows — a job, or a metered call outside one — the work
 * and the brand it was for replace it: the work names itself, and the row is
 * one line wide.
 */
function lineLabel(t: (key: string) => string, entry: LedgerEntry): string {
	if (!entry.detail) return stored(t, "ledger", entry.reason);
	const job = stored(t, "jobs", entry.detail);
	const work = job === entry.detail ? stored(t, "meter", entry.detail) : job;
	return entry.entity ? `${work} · ${entry.entity}` : work;
}

/**
 * Where the credits went.
 *
 * The ledger is shown in full rather than as a balance, because "what did my
 * $49 buy" is the question this exists to answer, and a single number answers
 * it badly.
 *
 * Two tables, and the split is the one the customer is billed by: model usage
 * is real money spent at the model's published price, the ledger is credits.
 * Sampling appears in the first and not the second, which is why a balance that
 * has not moved can sit above a bill that has.
 */
export function UsagePanel({ org }: { org: Org }) {
	const t = useT();
	const { locale } = useI18n();
	const credits = useInfiniteQuery({
		queryKey: [...keys.org(org.id), "credits"],
		queryFn: ({ pageParam }) => api.credits(org.id, pageParam),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (last) => last.next_cursor ?? undefined,
		...read,
	});
	const usage = useQuery({ queryKey: [...keys.org(org.id), "usage"], queryFn: () => api.usage(org.id), ...read });
	const lots = credits.data?.pages[0]?.credits;

	return (
		<>
			{/* The number, in its two lifetimes — what lapses with the period and
			    what was bought — on the card the number is on, with the way to
			    add to the second under it. */}
			<div className="stats">
				<CreditsStat org={org} credits={lots} state={org.subscription_state} foot={t("billing.balance")} />
			</div>

			<Section title={t("billing.usage")}>
				<Async
					state={usage}
					empty={
						<EmptyState size="compact" title={t("billing.usageEmpty")} description={t("billing.usageEmptyBody")} />
					}
				>
					{(data) =>
						data.models.length === 0 ? (
							<EmptyState size="compact" title={t("billing.usageEmpty")} description={t("billing.usageEmptyBody")} />
						) : (
							<div className="table-scroll bleed-gutter" style={{ marginBottom: "var(--s-9)" }}>
								<Table variant="resource">
									<TableHeader>
										<TableRow>
											<TableHead>{t("billing.model")}</TableHead>
											<TableHead className="num">{t("billing.calls")}</TableHead>
											<TableHead className="num">{t("billing.tokens")}</TableHead>
											<TableHead className="num">{t("billing.spend")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.models.map((row) => (
											<TableRow key={row.model}>
												<TableCell>{row.model}</TableCell>
												<TableCell className="num">{fmt.number(locale, row.calls)}</TableCell>
												<TableCell className="num">
													{fmt.number(locale, row.input_tokens + row.output_tokens)}
												</TableCell>
												<TableCell className="num">{fmt.usd(locale, row.billed_micros)}</TableCell>
											</TableRow>
										))}
										<TableRow>
											<TableCell colSpan={3}>
												<strong>{t("billing.usageTotal", { days: String(data.days) })}</strong>
											</TableCell>
											<TableCell className="num">
												<strong>{fmt.usd(locale, data.total_micros)}</strong>
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</div>
						)
					}
				</Async>
			</Section>

			<Section title={t("billing.history")}>
				<Async
					state={credits}
					empty={<EmptyState size="compact" title={t("billing.empty")} description={t("billing.emptyBody")} />}
				>
					{(data) => {
						const entries = data.pages.flatMap((page) => page.entries);
						return entries.length === 0 ? (
							<EmptyState size="compact" title={t("billing.empty")} description={t("billing.emptyBody")} />
						) : (
							<>
								<div className="table-scroll bleed-gutter">
									<Table variant="resource">
										<TableHeader>
											<TableRow>
												<TableHead>{t("table.time")}</TableHead>
												<TableHead className="num">{t("table.change")}</TableHead>
												<TableHead>{t("table.reason")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{entries.map((entry) => (
												<TableRow key={entry.id}>
													<TableCell className="mono whitespace-nowrap">
														{fmt.dateTime(locale, entry.created_at)}
													</TableCell>
													<TableCell
														className="num mono"
														style={{ color: entry.delta > 0 ? "var(--success)" : undefined }}
													>
														{fmt.signed(locale, entry.delta)}
													</TableCell>
													{/* The line can be long, and a nowrap cell that sizes to
													    its text widens the table instead of clipping: zero max
													    width over the remaining space is what makes the fade
													    and its tooltip happen. */}
													<TableCell className="w-full max-w-0">
														<OverflowText label={lineLabel(t, entry)} />
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
								{credits.hasNextPage && (
									<Button
										variant="default"
										onClick={() => credits.fetchNextPage()}
										disabled={credits.isFetchingNextPage}
									>
										{t("table.more")}
									</Button>
								)}
							</>
						);
					}}
				</Async>
			</Section>
		</>
	);
}
